"use client";

import { Partner } from "@justhome/api-core";
import clsx from "clsx";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import Checkbox from "../Checkbox";
import { Locale, render } from "../lib/i18n";

const translations = {
  de: {
    form_consent_terms:
      "Ich möchte den für mich kostenlosen Service von Justhome rund um Immobilienfinanzierung, -suche und -information nutzen. Es gelten die <a class='text-primary-800' href='https://justhome.com/datenschutz' target='_blank'>Datenschutzbestimmungen</a> und <a href='https://justhome.com/agb' target='_blank' class='text-primary-800'>AGB</a> der Justhome GmbH.",
    form_consent_terms_partner:
      "Ich möchte den für mich kostenlosen Service von Justhome rund um Immobilienfinanzierung, -suche und -information nutzen. Justhome hält für einen reibungslosen Ablauf meinen Immobilienmakler informiert. Mit der Übermittlung meiner Daten an die Justhome GmbH bin ich einverstanden. Es gelten die <a class='text-primary-800' href='https://justhome.com/datenschutz' target='_blank'>Datenschutzbestimmungen</a> und <a href='/agb' target='_blank' class='text-primary-800'>AGB</a> der Justhome GmbH.",
    form_consent_terms_partner_seller_leads:
      "Ich möchte den für mich kostenlosen Service von Justhome rund um Immobilienfinanzierung, -suche und -information nutzen. Justhome hält für einen reibungslosen Ablauf meinen Immobilienmakler informiert, u.a. zu Leistbarkeit, Kaufbudget und Verkaufsabsicht. Mit der Übermittlung meiner Daten an die Justhome GmbH bin ich einverstanden. Es gelten die <a class='text-primary-800' href='https://justhome.com/datenschutz' target='_blank'>Datenschutzbestimmungen</a> und <a href='https://justhome.com/agb' target='_blank' class='text-primary-800'>AGB</a> der Justhome GmbH.",
    form_consent_terms_partner_external_advisor:
      "Ich möchte den für mich kostenlosen Service von Justhome und seinen <a class='text-primary-800' href='https://justhome.com/finanzierungspartner' target='_blank'>Finanzierungspartnern</a> rund um die Immobilienfinanzierung, -suche und -information nutzen. Ich bin damit einverstanden, dass mein Makler für einen reibungslosen Ablauf informiert bleibt und dass meine Daten an die Justhome GmbH &amp; ihre Finanzierungspartner übermittelt werden. Es gelten die <a class='text-primary-800' href='https://justhome.com/datenschutz' target='_blank'>Datenschutzrichtlinien</a> und <a href='https://justhome.com/agb' target='_blank' class='text-primary-800'>AGBs</a> der Justhome GmbH.",
    form_consent_terms_partner_external_advisor_seller_leads:
      "Ich möchte den für mich kostenlosen Service von Justhome und seinen <a class='text-primary-800' href='https://justhome.com/finanzierungspartner' target='_blank'>Finanzierungspartnern</a> rund um die Immobilienfinanzierung, -suche und -information nutzen. Ich bin damit einverstanden, dass mein Makler für einen reibungslosen Ablauf informiert bleibt, u.a. zu Leistbarkeit, Kaufbudget und Verkaufsabsicht, und dass meine Daten an die Justhome GmbH &amp; ihre Finanzierungspartner übermittelt werden. Es gelten die <a class='text-primary-800' href='https://justhome.com/datenschutz' target='_blank'>Datenschutzrichtlinien</a> und <a href='https://justhome.com/agb' target='_blank' class='text-primary-800'>AGBs</a> der Justhome GmbH.",
    partner_contact_sharing_opt_in:
      "Ich bin damit einverstanden, dass meine Kontaktdaten an {partner} zum Zwecke der Kontaktaufnahme weitergeleitet werden.",
    partner_budget_sharing_opt_in:
      "Ich bin damit einverstanden, dass meine Kontaktdaten und mein maximales Kaufbudget an {partner} zum Zwecke der Kontaktaufnahme weitergeleitet werden.",
    opt_in_error:
      "Um den Service nutzen zu können, benötigen wir die Zustimmung zu den Nutzungsbedingungen und der Datenweitergabe.",
  },
  en: {
    form_consent_terms:
      "I would like to use Justhome’s service, which is free of charge for me, regarding real estate financing, search, and information. The <a class='text-primary-800' href='https://justhome.com/en/data-privacy' target='_blank'>privacy policy</a> and <a href='https://justhome.com/en/terms-and-conditions' target='_blank' class='text-primary-800'>terms and conditions</a> of Justhome GmbH apply.",
    form_consent_terms_partner:
      "I would like to use Justhome’s service, which is free of charge for me, regarding real estate financing, search, and information. Justhome keeps my real estate agent informed to ensure a smooth process. I agree to the transmission of my data to Justhome GmbH. The <a class='text-primary-800' href='https://justhome.com/en/data-privacy' target='_blank'>privacy policy</a> and <a href='https://justhome.com/en/terms-and-conditions' target='_blank' class='text-primary-800'>terms and conditions</a> of Justhome GmbH apply.",
    form_consent_terms_partner_seller_leads:
      "I would like to use Justhome’s service, which is free of charge for me, regarding real estate financing, search, and information. Justhome keeps my real estate agent informed to ensure a smooth process, including about affordability, purchase budget, and intent to sell. I agree to the transmission of my data to Justhome GmbH. The <a class='text-primary-800' href='https://justhome.com/en/data-privacy' target='_blank'>privacy policy</a> and <a href='https://justhome.com/en/terms-and-conditions' target='_blank' class='text-primary-800'>terms and conditions</a> of Justhome GmbH apply.",
    form_consent_terms_partner_external_advisor:
      "I would like to use the service of Justhome and its <a class='text-primary-800' href='https://justhome.com/en/financingpartner' target='_blank'>financing partners</a>, which is free of charge for me, regarding real estate financing, search, and information. I agree that my agent remains informed to ensure a smooth process and that my data will be transmitted to Justhome GmbH and its financing partners. The <a class='text-primary-800' href='https://justhome.com/en/data-privacy' target='_blank'>privacy policy</a> and <a href='https://justhome.com/en/terms-and-conditions' target='_blank' class='text-primary-800'>terms and conditions</a> of Justhome GmbH apply.",
    form_consent_terms_partner_external_advisor_seller_leads:
      "I would like to use the service of Justhome and its <a class='text-primary-800' href='https://justhome.com/en/financingpartner' target='_blank'>financing partners</a>, which is free of charge for me, regarding real estate financing, search, and information. I agree that my agent remains informed to ensure a smooth process, including about affordability, purchase budget, and intent to sell, and that my data will be transmitted to Justhome GmbH and its financing partners. The <a class='text-primary-800' href='https://justhome.com/en/data-privacy' target='_blank'>privacy policy</a> and <a href='https://justhome.com/en/terms-and-conditions' target='_blank' class='text-primary-800'>terms and conditions</a> of Justhome GmbH apply.",
    partner_contact_sharing_opt_in:
      "I agree that my contact details will be forwarded to {partner} for the purpose of making contact.",
    partner_budget_sharing_opt_in:
      "I agree that my contact details and my maximum purchase budget will be forwarded to {partner} for the purpose of making contact.",
    opt_in_error:
      "To use the service, we need your consent to the terms of use and data transfer.",
  },
};

export const getFormConsentText = ({
  locale,
  requireContactSharingOptIn = false,
  requireBudgetSharingOptIn = false,
  partner,
}: {
  locale: Locale;
  requireContactSharingOptIn?: boolean;
  requireBudgetSharingOptIn?: boolean;
  partner?: Partner;
}): string => {
  const t = translations[locale];
  const consentText =
    t[
      getFormConsentKey(
        !!partner,
        partner?.isExternalAdvisorEnabled,
        partner?.hasSellerLeadsEnabled,
      )
    ];
  const optInKey = getOptInKey(
    requireBudgetSharingOptIn,
    requireContactSharingOptIn,
  );
  let optInText = "";
  if (optInKey) {
    optInText = render(t[optInKey], {
      partner: partner?.name ?? "Justhome GmbH",
    });
  }
  return [consentText, optInText].filter(Boolean).join(" ");
};

const getFormConsentKey = (
  isPartner = false,
  isExternalAdvisorEnabled = false,
  hasSellerLeadsEnabled = false,
): keyof typeof translations.de => {
  if (!isPartner) {
    return "form_consent_terms";
  }
  if (isExternalAdvisorEnabled) {
    return hasSellerLeadsEnabled
      ? "form_consent_terms_partner_external_advisor_seller_leads"
      : "form_consent_terms_partner_external_advisor";
  }
  return hasSellerLeadsEnabled
    ? "form_consent_terms_partner_seller_leads"
    : "form_consent_terms_partner";
};

const getOptInKey = (
  requireBudgetSharingOptIn: boolean,
  requireContactSharingOptIn: boolean,
): keyof typeof translations.de | null => {
  if (requireBudgetSharingOptIn) {
    return "partner_budget_sharing_opt_in";
  }
  if (requireContactSharingOptIn) {
    return "partner_contact_sharing_opt_in";
  }
  return null;
};

export interface FormLegalDisclaimerProps<TFieldValues extends FieldValues> {
  locale: Locale;
  partner?: Partner;
  requireBudgetSharingOptIn?: boolean;
  control?: Control<TFieldValues, any>;
  optInFieldName?: Path<TFieldValues>;
  requireContactSharingOptIn?: boolean;
  className?: string;
  disclaimerClassName?: string;
}

const FormLegalDisclaimer = <TFieldValues extends FieldValues>({
  locale,
  partner,
  requireBudgetSharingOptIn = false,
  control,
  optInFieldName,
  requireContactSharingOptIn = false,
  className,
  disclaimerClassName,
}: FormLegalDisclaimerProps<TFieldValues>) => {
  const t = translations[locale];

  const disclaimerKey = getFormConsentKey(
    !!partner,
    partner?.isExternalAdvisorEnabled,
    partner?.hasSellerLeadsEnabled,
  );
  const disclaimerText = t[disclaimerKey];

  const optInKey = getOptInKey(
    requireBudgetSharingOptIn,
    requireContactSharingOptIn,
  );

  if (optInKey && control) {
    const optInText = render(t[optInKey], {
      partner: partner?.name ?? "Justhome GmbH",
    });
    return (
      <div className={className}>
        <p
          className={clsx("mb-3s text-app-body-2", disclaimerClassName)}
          dangerouslySetInnerHTML={{
            __html: disclaimerText,
          }}
        />
        <Controller<TFieldValues>
          control={control}
          name={optInFieldName as Path<TFieldValues>}
          rules={{ required: t.opt_in_error }}
          render={({ field, fieldState }) => (
            <Checkbox
              size="sm"
              className="mb-3s"
              checked={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              errorMessage={fieldState.error?.message}
            >
              {optInText}
            </Checkbox>
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx("mt-2xs text-app-body-2", className)}
      dangerouslySetInnerHTML={{
        __html: disclaimerText,
      }}
    />
  );
};

export default FormLegalDisclaimer;
